import type React from "react";
import { useState } from "react";

import type { ConnectedProjectDto, UserProjectDto } from "@/api/types";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Accordion } from "@/design-system/Accordion";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

enum EditType {
  ALL_PROJECTS = "ALL_PROJECTS",
  CURRENT_PROJECT = "CURRENT_PROJECT",
}

type EditUserNotificationSettingsConfirmationModal = ModalBaseProps & {
  projects: UserProjectDto[] | ConnectedProjectDto[];
  isSubmitting: boolean;
  onConfirm: (applyToAllProjects: boolean) => void;
};

export function EditUserNotificationSettingsConfirmationModal({
  isOpened,
  onOpenChange,
  projects,
  isSubmitting,
  onConfirm,
}: EditUserNotificationSettingsConfirmationModal): React.ReactNode {
  const [selectedType, setSelectedType] = useState<EditType>(EditType.CURRENT_PROJECT);

  const { t } = useTranslation();

  return (
    <Modal.Root
      data-testid="confirm-edit-modal"
      title={t("page.notification-settings.edit-confirmation-modal.title")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
    >
      <Modal.Body>
        <Stack gap="md">
          <p className="text-body">{t("page.notification-settings.edit-confirmation-modal.body")}</p>
          <FormRadioCardGroup>
            <RadioCard
              name="selectedType"
              value={EditType.ALL_PROJECTS}
              checked={selectedType === EditType.ALL_PROJECTS}
              onChange={() => setSelectedType(EditType.ALL_PROJECTS)}
              label={t("page.notification-settings.edit-confirmation-modal.form.option.apply-all-projects", {
                count: projects.length,
              })}
              data-testid="apply-to-all-projects-option"
            />
            <RadioCard
              name="selectedType"
              value={EditType.CURRENT_PROJECT}
              checked={selectedType === EditType.CURRENT_PROJECT}
              onChange={() => setSelectedType(EditType.CURRENT_PROJECT)}
              label={t("page.notification-settings.edit-confirmation-modal.form.option.apply-current-project")}
              data-testid="apply-to-current-project-option"
            />
          </FormRadioCardGroup>
          {selectedType === EditType.ALL_PROJECTS ? (
            <Accordion.Root>
              <Accordion.Item value="connected-projects-info">
                <Accordion.Trigger>
                  {t("page.notification-settings.edit-confirmation-modal.accordion.edit-details.title")}
                </Accordion.Trigger>
                <Accordion.Content>
                  <Stack gap="sm">
                    <p>{t("page.notification-settings.edit-confirmation-modal.accordion.edit-details.intro")}</p>
                    <ul className="list-inside list-disc pl-space-md">
                      <li>{t("page.notification-settings.form.messages.new-general-message")}</li>
                      <li>{t("page.notification-settings.form.messages.new-general-message-comment")}</li>
                      <li>{t("page.notification-settings.form.chat.new-chat")}</li>
                      <li>{t("page.notification-settings.form.email.community-summary")}</li>
                      <li>{t("page.notification-settings.form.email.monthly-report")}</li>
                      <li>{t("page.notification-settings.form.tickets.assigned-to-me.is-assigned")}</li>
                    </ul>
                    <p className="italic">
                      {t("page.notification-settings.edit-confirmation-modal.accordion.edit-details.disclaimer")}
                    </p>
                  </Stack>
                </Accordion.Content>
              </Accordion.Item>
            </Accordion.Root>
          ) : (
            <div className="h-control-lg" />
          )}
        </Stack>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button
            onClick={() => onConfirm(selectedType === EditType.ALL_PROJECTS)}
            isLoading={isSubmitting}
            data-testid="confirm-btn"
          >
            {selectedType === EditType.CURRENT_PROJECT
              ? t("page.notification-settings.edit-confirmation-modal.btn.apply-to-current-project")
              : t("page.notification-settings.edit-confirmation-modal.btn.apply-to-all-projects")}
          </Button>
        </Modal.Close>
      </Modal.Actions>
    </Modal.Root>
  );
}
